import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEsCL from '@angular/common/locales/es-CL';

registerLocaleData(localeEsCL, 'es-CL');
import {
  provideRouter,
  withExperimentalAutoCleanupInjectors,
  Router,
} from '@angular/router';

import { routes } from './app.routes';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  apiBaseUrlInterceptor,
  authTokenInterceptor,
  httpErrorInterceptor,
  HTTP_ERROR_OPTIONS,
} from '@dgacontrol-frontend/core';
import { JwtModule } from '@auth0/angular-jwt';
import { BILLING_CHARGES_DEFAULT_ORDERING } from '@dgacontrol-frontend/billing-data-access';
import { environment } from '@dgacontrol-frontend/environments';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { AppThemePreset } from '@dgacontrol-frontend/shared/ui/theme';
import * as Sentry from '@sentry/angular';

export function tokenGetter() {
  return localStorage.getItem('token_jwt');
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: tokenGetter, //() => localStorage.getItem('token_jwt'), //tokenGetter,
          allowedDomains: [environment.api.domain],
          disallowedRoutes: [
            environment.api.baseUrl + 'v3/accounts/login/',
            environment.api.baseUrl + 'v3/accounts/jtw/token/refresh/',
          ],
        },
      }),
      MessageService,
    ),

    // Functional interceptors all run BEFORE the DI-based ones, and
    // `withInterceptorsFromDi()` stays for `JwtModule.forRoot()` — the one that
    // actually attaches the token. Order inside the array is load-bearing:
    // `apiBaseUrlInterceptor` before JwtModule (its rewrite of the Orval clients'
    // relative `/vN/` URLs to absolute is what makes `allowedDomains` match), and
    // `authTokenInterceptor` after `httpErrorInterceptor` so it still sees errors
    // first (they bubble inner → outer) — http-error skips 401/403 assuming that.
    provideHttpClient(
      withInterceptors([
        httpErrorInterceptor,
        apiBaseUrlInterceptor,
        authTokenInterceptor,
      ]),
      withInterceptorsFromDi(),
    ),
    //importProvidersFrom(HttpClientModule),
    // admin = staff interno → muestra el detail del backend en los 5xx.
    { provide: HTTP_ERROR_OPTIONS, useValue: { verboseServerErrors: true } },
    // Admin lists setup/service/subscription charges ordered oldest-first —
    // the internal panel convention. Web overrides to `desc` (newest-first,
    // Prime Video pattern) in its own app.config.
    { provide: BILLING_CHARGES_DEFAULT_ORDERING, useValue: 'asc' },
    // Without the cleanup feature the router NEVER destroys a route injector, so the
    // stores scoped to the authenticated shell in `app.routes.ts` would outlive the
    // session that filled them. Why it is load-bearing → `AuthService.logout()`.
    // Experimental since 21.1: re-check on every Angular upgrade.
    provideRouter(routes, withExperimentalAutoCleanupInjectors()),
    // PrimeNG 21 uses native CSS animations — no longer needs provideAnimations*
    // (removed per the v21 migration; the @angular/animations provider was
    // deprecated in 20.2). See providePrimeNG below.
    providePrimeNG({
      theme: {
        preset: AppThemePreset,
        options: {
          darkModeSelector: '.app-dark',
        },
      },
      ripple: true,
      inputVariant: 'outlined',
    }),
    MessageService,
    { provide: LOCALE_ID, useValue: 'es-CL' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // Force Sentry.TraceService instantiation at startup (it wires router
    // tracing via its constructor). Runs in injection context.
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
